/*
 * @Author: chengyu
 * @Date: 2022-10-11 15:04:39
 * @Description: geoHash + 缓存
 */
import DiskLRUCache, { MpTypeEnum } from '@hb/map-disk-lru-cache';
import LatLonGeoHash from '@hb/map-latlon-geohash';
import { getContext, getContextEnv, getDebug, getEnv } from '../config';
import { ContextEnvEnum } from '../enums';
import { debugRgcShowModal, showToast } from './debug';
import { mapApiEmitter } from './emitter';
import { isObject } from './is';
import { mapError, mapLog } from './logger';
import { cutOffProperty } from './index';
var cacheKey = 'mapi';
// 缓存控制器池
var cacheGeoHashPool = new Map();
function checkMp() {
    var contextEnv = getContextEnv();
    if (contextEnv === ContextEnvEnum.TARO) {
        return MpTypeEnum.taro;
    }
    else if (contextEnv === ContextEnvEnum.ALIPAY) {
        return MpTypeEnum.alipay;
    }
    else if (contextEnv === ContextEnvEnum.WECHAT) {
        return MpTypeEnum.wechat;
    }
    else if (contextEnv === ContextEnvEnum.BROWSER) {
        return MpTypeEnum.browser;
    }
    else {
        throw new Error("CacheGeoHash \u5728\u4E0D\u652F\u6301(".concat(contextEnv, ")\u7684\u73AF\u5883\u4E0B\u8FD0\u884C"));
    }
}
function debugTtl(debug) {
    // 测试 5分钟
    if (debug)
        return 5 * 60 * 1000;
    // 默认2天
    return 2 * 24 * 60 * 60 * 1000;
}
var CacheGeoHash = /** @class */ (function () {
    function CacheGeoHash(options) {
        var _this = this;
        var _a = options.namespace, namespace = _a === void 0 ? cacheKey : _a;
        var env = getEnv();
        var contextEnv = getContextEnv();
        var debug = getDebug();
        this.namespace = "".concat(namespace, ":").concat(env);
        // 高精度
        this.highPrecision = debug ? 7 : 9;
        // 低精度
        this.lowPrecision = debug ? 6 : 7;
        // 初始化 缓存管理器
        this.diskLruCache = new DiskLRUCache(this.namespace, {
            debug: false,
            // 10条数据
            max: 10,
            // 过期时间
            ttl: debugTtl(debug),
            // 存储上限KB，超过这个值，就不会继续存储了
            // 浏览器storage上限，暂认为是5M，用来做本地缓存的空间，不超过3M
            limitSize: contextEnv === ContextEnvEnum.BROWSER ? 1024 * 3 : 1024 * 5,
            // get调用10次，后即过期
            limitCount: 10,
            mp: checkMp(),
            mpApi: getContext(),
        });
        // Events
        // 清空逆地理缓存
        mapApiEmitter.on('DISK_LRU_CACHE_CLEAR', function () {
            // 清空缓存
            _this.diskLruCache.clear();
        });
    }
    /**
     * 设置标签
     * @param tag
     */
    CacheGeoHash.prototype.setTag = function (tag) {
        this.tag = tag;
    };
    Object.defineProperty(CacheGeoHash.prototype, "ntKey", {
        /**
         * namespace+tag cacheKey 前缀
         */
        get: function () {
            if (this.tag)
                return "".concat(this.namespace, ":").concat(this.tag);
            return this.namespace;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * 将key加上namespace后再设置缓存，作用数据隔离
     * @param key
     */
    CacheGeoHash.prototype.getKey = function (key) {
        // 具有namespace的key，直接返回
        if (key.startsWith(this.ntKey))
            return key;
        // 没有namespace的key，加上namespace后返回
        return "".concat(this.ntKey, ":").concat(key);
    };
    CacheGeoHash.prototype.encode = function (point, precision) {
        var lat = point.lat, lng = point.lng;
        return LatLonGeoHash.encode(lat, lng, precision);
    };
    CacheGeoHash.prototype.set = function (point, value) {
        try {
            var _value = cutOffProperty(value);
            var key = this.encode(point, this.highPrecision);
            if (key)
                this.diskLruCache.set(this.getKey(key), _value);
        }
        catch (e) {
            mapError('逆地理缓存，写入失败');
        }
    };
    /**
     * 高精度缓存
     * GeoHash 精度为 highPrecision
     */
    CacheGeoHash.prototype.get = function (point) {
        try {
            var key = this.encode(point, this.highPrecision);
            mapLog('尝试命中高精度缓存(get):', [key]);
            if (key) {
                var result = this.diskLruCache.get(this.getKey(key));
                if (isObject(result)) {
                    this.debug(key, '高精度缓存');
                    return cutOffProperty(result);
                }
            }
        }
        catch (e) {
            mapError('逆地理缓存，读取高精度缓存失败', e);
        }
        return undefined;
    };
    /**
     * 粗精度缓存
     * GeoHash 精度为 lowPrecision
     *
     * 列如: 取高精度(8)的前六位，就是粗精度(6)的值
     * @param point
     */
    CacheGeoHash.prototype.crude = function (point) {
        var _this = this;
        try {
            var key_1 = this.encode(point, this.lowPrecision);
            var originKey_1 = '';
            mapLog('尝试命中低精度缓存(crude):', [key_1]);
            if (key_1) {
                var result = this.diskLruCache.find(function (_, k) {
                    var success = k.startsWith(_this.getKey(key_1));
                    if (success)
                        originKey_1 = k;
                    return success;
                });
                if (isObject(result)) {
                    if (originKey_1)
                        this.debug(originKey_1, '低精度缓存');
                    return cutOffProperty(result);
                }
            }
        }
        catch (e) {
            mapError('逆地理缓存，读取粗缓存失败', e);
        }
        return undefined;
    };
    /**
     * 调试
     */
    CacheGeoHash.prototype.debug = function (key, precision) {
        var _this = this;
        if (getDebug()) {
            try {
                var _key = this.getKey(key);
                var info = this.diskLruCache.getInfo(_key);
                var cacheCounts = this.diskLruCache.dump().length;
                debugRgcShowModal({
                    cacheCounts: cacheCounts,
                    cancel: function () {
                        // 清空缓存
                        _this.diskLruCache.clear();
                        showToast('缓存已清空');
                    },
                    currentCount: info === null || info === void 0 ? void 0 : info.currentCount,
                    key: _key,
                    limitCount: info === null || info === void 0 ? void 0 : info.limitCount,
                    precision: precision,
                    start: info === null || info === void 0 ? void 0 : info.start,
                    ttl: info === null || info === void 0 ? void 0 : info.ttl,
                });
            }
            catch (e) {
                mapError('逆地理缓存, debug', e);
            }
        }
    };
    return CacheGeoHash;
}());
export { CacheGeoHash };
/**
 * 创建缓存控制器
 * 一个命名空间`namespace`对应一个缓存控制器`CacheGeoHash`
 * @param options
 */
export function createCacheGeoHash(options) {
    try {
        var _a = options.namespace, namespace = _a === void 0 ? cacheKey : _a, tag = options.tag;
        var cache = cacheGeoHashPool.get(namespace);
        if (cache) {
            cache.setTag(tag);
            return cache;
        }
        cache = new CacheGeoHash(options);
        cache.setTag(tag);
        cacheGeoHashPool.set(namespace, cache);
        return cache;
    }
    catch (e) {
        mapError('createCacheGeoHash', e);
    }
    return undefined;
}
//# sourceMappingURL=data:application/json;base64,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